import React from 'react';
import type { AddGroupFormColumn } from '#components/ProTablePlusForm/addGroupTable';
import { FilterType } from '@bedrock/eed-react/lib/components/ProFilter/interface';
import { Radio, Tag } from '@bedrock/components';
import {
  ThumbsUp,
  ThumbsDown,
  Heart,
  Check,
  Close,
} from '@bedrock/icons-react';
import { EllipsisTooltip } from '@mpc/components';
import ExpertCell from '../cells/ExpertCell';
import CooperationProjectCell from '../cells/CooperationProjectCell';
import HistoricalCooperationCell from '../cells/HistoricalCooperationCell';
import SupplierCell from '../cells/SupplierCell';
import PlatformPriceCell from '../cells/PlatformPriceCell';
import PlatformCPMCell from '../cells/PlatformCPMCell';
import CustomScreeningCell from '../cells/CustomScreeningCell';
import { CurrencyEnum, PlanSuggestionState } from '#services/api';
import RecommendReturnCell from '../cells/RecommendReturnCell';
import { KOLDemandCategory } from './type';
import InitialQuoteCell from '../cells/InitialQuoteCell';

const PlanSuggestionMap: Record<
  PlanSuggestionState,
  { text: string; color: any; icon: React.ReactNode }
> = {
  3: {
    text: '强推',
    color: 'light-green',
    icon: <ThumbsUp />,
  },
  2: { text: '建议', color: 'light-blue', icon: <Heart /> },
  1: {
    text: '不推荐',
    color: 'light-grey',
    icon: <ThumbsDown />,
  },
};

export const PlanSuggestionOptions = Object.entries(PlanSuggestionMap)
  .map(([key, value]) => ({
    name: value.text,
    icon: value.icon,
    value: Number(key),
  }))
  .reverse();

/**
 * 从度量标签存储（metric_tags / MetricTagStorage）中提取全部星图标签名
 * 结构：{ xingtu_tag_info: [{ tag_category, xingtu_tag_list, game_code, recent_release_time }] }
 */
export const getMetricTagNames = (metricTags: {
  xingtu_tag_info?: { xingtu_tag_list?: string[] }[];
}): string[] => {
  if (!metricTags?.xingtu_tag_info?.length) {
    return [];
  }
  return metricTags.xingtu_tag_info.flatMap(
    (info) => info?.xingtu_tag_list || []
  );
};

// ---------------- ExpertView ----------------
/**
 * 备注列（需求方备注/策划备注）只读态省略行数。
 * 达人视图虚拟表格固定行高 itemHeight=180（见 ExpertView），单元格内容区高度 164px；
 * 单元格 line-height 22px，7 行 = 154px 不溢出，故取 7 行。
 */
const REMARK_ELLIPSIS_CLAMP = 7;

export const getKOLColumnsConfig = ({
  viewCurrency,
  view,
  category,
}: {
  viewCurrency: CurrencyEnum;
  view: 'expert' | 'supplier';
  category: KOLDemandCategory;
}): Record<string, AddGroupFormColumn> => {
  return {
    orderNumber: {
      title: '#',
      dataIndex: 'orderNumber',
      width: 42,
      fixed: 'left' as const,
      render: (value, record, index) => {
        return index + 1;
      },
    },
    expert: {
      title: '达人',
      dataIndex: 'main_platform_link',
      width: 256,
      fixed: 'left' as const,
      className: 'align-top',
      render: (value, record, index) => {
        const enabledTags = [
          view === 'expert'
            ? record.$by_plan && 'designated'
            : record.by_plan && 'designated',
          view === 'expert'
            ? record.$by_supplier && 'supplier_recommend'
            : record.by_supplier && 'supplier_recommend',
        ].filter(Boolean) as any[];
        return (
          <ExpertCell
            name={view === 'expert' ? undefined : `达人${record.$index}`}
            enabledTags={enabledTags}
            link={record.main_platform_link}
            platform={
              view === 'expert'
                ? record.$main_platform || record.main_platform
                : record.main_platform
            }
            account={
              view === 'expert'
                ? record.$main_platform_account || record.main_platform_account
                : record.main_platform_account
            }
          />
        );
      },
    },
    demandConfirm: {
      title: '需求方确认',
      dataIndex: 'principal_confirm',
      width: 128,
      fixed: 'left',
      className: 'align-top',
      editRender: (config: any) => {
        return {
          name: 'single-checkbox',
          editable: true,
          props: {
            disabled: !!config?.row?.submit_demand,
            children: '确认',
          },
        };
      },
      batchEditRender: {
        editable: true,
        name: 'select',
        props: {
          options: [
            { name: '确认', value: true },
            { name: '不确认', value: false },
          ],
        },
      },
      render: (value, record) => (
        <Tag
          text={value ? '确认' : '未确认'}
          color={value ? 'green' : 'grey'}
          icon={value ? <Check /> : <Close />}
          border={false}
          size="small"
        />
      ),
    },

    principalRemark: {
      title: '需求方备注',
      dataIndex: 'principal_remark',
      width: 200,
      fixed: 'left',
      className: 'align-top',
      editRender: (config: any) => ({
        name: 'mpc-textArea',
        editable: true,
        props: {
          textAreaProps: { maxLength: 200 },
          maxLength: 200,
          placeholder: '请输入需求方备注',
          // 与「需求方确认」列同一 disabled 规则：已提交需求方（submit_demand）的行不可再编辑备注
          disabled: !!config?.row?.submit_demand,
        },
      }),
      batchEditRender: {
        editable: true,
        name: 'input',
        props: {
          maxLength: 200,
          placeholder: '请输入需求方备注',
        },
      },
      render: (value) =>
        value ? (
          <EllipsisTooltip text={value} clamp={REMARK_ELLIPSIS_CLAMP} />
        ) : (
          '--'
        ),
    },

    planSuggestion: {
      title: '策划预筛选',
      dataIndex: 'plan_suggestion',
      width: 140,
      fixed: 'left',
      className: 'align-top',
      editRender: () => ({
        name: 'radio-group',
        editable: true,
        rule: {
          required: true,
          message: '策划预筛选不能为空',
        },
        props: {
          options: Object.entries(PlanSuggestionMap)
            .map(([key, value]) => ({
              name: (
                <Tag
                  text={value.text}
                  color={value.color as any}
                  size="xSmall"
                  icon={value.icon}
                  className="whitespace-nowrap"
                />
              ),
              value: Number(key),
            }))
            .reverse(),
        },
      }),
      batchEditRender: {
        editable: true,
        name: 'select',
        props: {
          options: PlanSuggestionOptions,
          renderLabel: (option) => {
            return (
              <span className="inline-flex items-center gap-[4px] text-color-1">
                {option.icon}
                {option.name}
              </span>
            );
          },
        },
      },
      render: (value) => {
        if (value && !isNaN(Number(value))) {
          return (
            <Tag
              text={PlanSuggestionMap[Number(value)].text}
              color={PlanSuggestionMap[Number(value)].color.replace(
                'light-',
                ''
              )}
              size="small"
              icon={PlanSuggestionMap[Number(value)].icon}
              className="whitespace-nowrap"
            />
          );
        }

        return '--';
      },
    },
    planRemark: {
      title: '策划备注',
      dataIndex: 'plan_remark',
      width: 200,
      fixed: 'left',
      className: 'align-top',
      editRender: () => ({
        name: 'mpc-textArea',
        editable: true,
        props: {
          textAreaProps: { maxLength: 200 },
          maxLength: 200,
          placeholder: '请输入策划备注',
        },
      }),
      batchEditRender: {
        editable: true,
        name: 'input',
        props: {
          maxLength: 200,
          placeholder: '请输入策划备注',
        },
      },
      render: (value) =>
        value ? (
          <EllipsisTooltip text={value} clamp={REMARK_ELLIPSIS_CLAMP} />
        ) : (
          '--'
        ),
    },
    metricTag: {
      title: '度量效果标签',
      dataIndex: 'metric_tags',
      // ProTablePlusForm virtual.maxRows<=0 恒开 antd 虚拟表格，列必须有显式 width（否则列宽折叠为 0）；
      // 300 = 「最近一次度量时间：yyyy-MM-dd HH:mm:ss」nowrap 单行宽度 + 内边距，标签区 flex-wrap 自适应换行
      width: 300,
      className: 'align-top',
      render: (value) => {
        const tagInfos = value?.xingtu_tag_info || [];
        const recentReleaseTime = tagInfos.find(
          (info: any) => info?.recent_release_time
        )?.recent_release_time;
        if (!tagInfos.length) {
          return '--';
        }
        return (
          <div className="flex h-[164px] flex-col gap-[4px] overflow-y-auto pr-[4px]">
            {tagInfos.map((info: any, index: number) => (
              <div
                key={`${info.tag_category}-${index}`}
                className="flex flex-col gap-[2px]"
              >
                <span className="text-color-3">{info.tag_category}</span>
                <div className="flex flex-wrap gap-[4px]">
                  {(info.xingtu_tag_list || []).map(
                    (tag: string, tagIndex: number) => (
                      <Tag
                        key={`${tag}-${tagIndex}`}
                        text={tag}
                        size="small"
                        className="whitespace-nowrap"
                      />
                    )
                  )}
                </div>
              </div>
            ))}
            {recentReleaseTime && (
              <span className="text-color-3 whitespace-nowrap">
                最近一次度量时间：{recentReleaseTime}
              </span>
            )}
          </div>
        );
      },
    },
    cooperationProject: {
      title: '合作项目',
      dataIndex: 'main_platform_quote_item',
      width: 224,
      className: 'align-top',
      render: (value, record) => (
        <CooperationProjectCell
          mainPlatformType={record.public_order_name}
          quoteProject={record.main_platform_quote_item_name || '--'}
        />
      ),
    },
    followerNum: {
      title: '粉丝数',
      dataIndex: ['demand_source_kol_content_vo', 'follower_num'],
      width: 120,
      className: 'align-top',
      align: 'right' as const,
      render: (value, record) =>
        record?.demand_source_kol_content_vo?.follower_num?.toLocaleString?.() ||
        '--',
    },
    history: {
      title: '历史合作(6个月内)',
      dataIndex: ['demand_source_kol_content_vo', 'is_coop_last6m'],
      className: 'align-top',
      width: 280,
      render: (value, record) => (
        <HistoricalCooperationCell
          isEmpty={
            record?.demand_source_kol_content_vo?.is_coop_last6m === undefined
          }
          hasHistory={
            record?.demand_source_kol_content_vo?.is_coop_last6m === '1'
          }
          avgPrice={
            category === 'live'
              ? record?.demand_source_kol_content_vo?.coop_price_cny_per_live
              : record?.demand_source_kol_content_vo?.coop_price_cny_per
          }
          priceCurrency={'RMB'} // 内容营销价格币种固定为人民币，来自服务端
          avgCPM={record?.demand_source_kol_content_vo?.coop_cpm_six_month}
          avgCPMPV={record?.demand_source_kol_content_vo?.cmp_pv_six_month}
          avgCPMUV={record?.demand_source_kol_content_vo?.cmp_uv_six_month}
          category={category}
          viewCurrency={viewCurrency}
        />
      ),
    },
    supplierCount: {
      title: '提报数量',
      dataIndex: 'quote_count',
      width: 72,
      className: 'align-top',
      align: 'left' as const,
      render: (value, record, index) => value ?? '--',
    },
    supplier: {
      title: '供应商',
      className: 'align-top',
      dataIndex: ['demand_source_supplier_vo', 'supplier_id'],
      width: 240,
      render: (value, record) => (
        <SupplierCell
          record={record}
          viewCurrency={viewCurrency}
          category={category}
        />
      ),
    },
    exclusiveSign: {
      title: '是否独家签约',
      dataIndex: 'exclusive_sign',
      width: 80,
      align: 'left' as const,
      className: 'align-top',
      render: (value) => (
        <Tag
          text={value ? '是' : '否'}
          color={value ? 'light-green' : 'light-grey'}
          size="xSmall"
        />
      ),
    },
    platformPrice: {
      title: '平台价格',
      dataIndex: 'platform_quote_price',
      width: 280,
      className: 'align-top',
      render: (value, record) => (
        <PlatformPriceCell
          starMapPrices={
            record?.demand_source_kol_content_vo?.all_quote_price_cny || []
          }
          starMapCurrency={'RMB'}
          supplierPrices={record?.platform_quote_price}
          supplierCurrency={record.quote_price_currency}
          quoteProject={record?.main_platform_quote_item}
          viewCurrency={viewCurrency}
        />
      ),
    },
    platformCPM: {
      title: '平台CPM',
      dataIndex: 'platformCPMPV',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <PlatformCPMCell
          starMapCPMList={
            record?.demand_source_kol_content_vo?.all_cmp_expected || []
          }
          supplierCPMList={record?.platform_cpm}
          quoteProject={record.main_platform_quote_item}
        />
      ),
    },
    platformCPMPV: {
      title: '平台CPM(PV)',
      dataIndex: 'platformCPMUV',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <PlatformCPMCell
          starMapCPMList={
            record?.demand_source_kol_content_vo?.all_cmp_expected || []
          }
          supplierCPMList={record?.platform_cpm_pv}
          quoteProject={record.main_platform_quote_item}
        />
      ),
    },
    platformCPMUV: {
      title: '平台CPM(UV)',
      dataIndex: 'platformCPM',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <PlatformCPMCell
          starMapCPMList={
            record?.demand_source_kol_content_vo?.all_cmp_expected || []
          }
          supplierCPMList={record?.platform_cpm_uv}
          quoteProject={record.main_platform_quote_item}
        />
      ),
    },
    customScreening: {
      title: '定制排查',
      dataIndex: 'customScreening',
      width: 400,
      className: 'align-top',
      render: (value, record) => (
        <CustomScreeningCell
          screeningName={
            <span className="font-bold">
              {record?.demand_source_custom_inspection_fields_value?.length
                ? `定制排查${record.$custom_inspection_index + 1}`
                : undefined}
            </span>
          }
          rules={(
            record?.demand_source_custom_inspection_fields_value || []
          ).map((f: any) => ({
            ruleName: f.field_name,
            description: f.field_name,
            passed: true,
            actualValue: f.value,
            requiredValue: f.plan_remark,
          }))}
        />
      ),
    },
    supplierName: {
      title: '供应商',
      dataIndex: 'demand_supplier_id',
      width: 120,
      fixed: 'left',
      className: 'align-top',
      render: (value, record) => {
        return (
          <span className="font-bold">
            {record.demand_source_supplier_vo?.supplier_name ?? '-'}
          </span>
        );
      },
    },
    initialQuote: {
      title: '初次报价',
      dataIndex: 'quote_price',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <InitialQuoteCell
          viewCurrency={viewCurrency}
          quotePrice={record.quote_price}
          quotePriceCurrency={record.quote_price_currency}
          cpm={record.cpm}
          cpmPV={record.cpm_pv}
          cpmUV={record.cpm_uv}
          category={category}
        />
      ),
    },
  };
};
